import Icon from 'components/base/Icon';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import { Modal } from '@linearb/linui-common';
import { useState } from 'react';
import ConnectGhesFormWrapper from './ConnectGhesFormWrapper';
import { Typography } from '@material-ui/core';
import {
  updateSensorAuthorization,
  deleteSensorAuthorization,
  getSensorAuthorizations
} from 'state/callers';
import { useDispatch } from 'react-redux';
import { LBButton } from '@linearb/buzz';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';

/**
 * GitstreamGithubConnectionButton component represents a button for connecting or disconnecting
 * the gitStream integration in the settings page.
 * @param {object} props - The component props.
 * @param {object} props.gitIntegration - The git integration object.
 * @param {Function} props.toggleModal - The function to toggle the modal.
 * @returns {JSX.Element} The GitstreamGithubConnectionButton component.
 */
export const GitstreamGithubConnectionButton = ({
  gitIntegration,
  toggleModal
}) => {
  const dispatch = useDispatch();
  const { getSettingOrDefinitionValue } = useSettingsService();
  const isGitHubAppIntegrationEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isGitHubAppIntegrationEnabled
  });
  const [showModal, setShowModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const {
    id,
    sensor_name,
    name: integrationName,
    self_hosted_uri,
    child_sensor_auth_id,
    sensor_id
  } = gitIntegration;

  const isConnected = child_sensor_auth_id !== null;

  const onClickButton = async e => {
    e.stopPropagation();

    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.GIT_INTEGRATIONS,
      integration_id: id,
      integration_name: integrationName,
      integration_type: sensor_name,
      click_type: isConnected ? 'connect_gitstream' : 'disconnect_gitstream'
    });

    if (!isConnected) {
      setShowModal(true);
    } else {
      setShowDisconnectModal(true);
    }
  };

  const onClose = e => {
    e?.stopPropagation?.();
    setShowModal(false);
  };

  const onDisconnectClose = e => {
    e?.stopPropagation?.();
    setShowDisconnectModal(false);
  };

  const handleDisconnect = async e => {
    e?.stopPropagation?.();

    await dispatch(
      updateSensorAuthorization(id, {
        child_sensor_auth_id: null
      })
    );
    await dispatch(deleteSensorAuthorization(child_sensor_auth_id));
    await dispatch(
      getSensorAuthorizations({
        withGitStreamEnabled: true,
        withoutGitHubApp: !isGitHubAppIntegrationEnabled
      })
    );
    setShowDisconnectModal(false);
  };

  return (
    <div>
      <LBButton
        size='sm'
        variant='outline'
        onClick={onClickButton}
        data-testid='gitstream-connect-btn'
      >
        <Icon name={isConnected ? 'UnlinkIcon' : 'LinkIcon'} size='smaller' />
        {`${isConnected ? 'Disconnect' : 'Connect'} gitStream`}
      </LBButton>
      <Modal
        open={showModal}
        onClose={onClose}
        showCloseButton
        onCloseButtonClick={onClose}
        onClick={e => e.stopPropagation()}
      >
        <ConnectGhesFormWrapper
          self_hosted_uri={self_hosted_uri}
          sensor_id={sensor_id}
          sensor_auth_id={id}
          onClose={onClose}
          integration={gitIntegration}
        />
      </Modal>

      <Modal
        open={showDisconnectModal}
        maxWidth='xs'
        showCloseButton
        onCloseButtonClick={onDisconnectClose}
        onClick={e => e.stopPropagation()}
        title={<Typography variant='h5'>Are you sure?</Typography>}
        actions={
          <>
            <LBButton variant='outline' onClick={onDisconnectClose}>
              Cancel
            </LBButton>
            <LBButton onClick={handleDisconnect}>Disconnect gitStream</LBButton>
          </>
        }
      >
        <Typography variant='h5'>
          Disconnecting gitStream will halt automation rules, leading to
          potential disruptions in your development pipeline, impacting cycle
          time and efficiency
        </Typography>
      </Modal>
    </div>
  );
};
